/**
 * React Query hooks for Adventure Clue Management (Creator)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cluesApi } from '@api/endpoints/clues'
import type {
  CreateClueRequest,
  UpdateClueRequest,
  ClueSettingsDto,
} from '@api/types/clues'

// ===================
// Query Keys
// ===================

export const CLUE_QUERY_KEYS = {
  all: (adventureId: string) => ['adventure-clues', adventureId] as const,
  list: (adventureId: string) => [...CLUE_QUERY_KEYS.all(adventureId), 'list'] as const,
  settings: (adventureId: string) => [...CLUE_QUERY_KEYS.all(adventureId), 'settings'] as const,
}

// ===================
// Query Hooks
// ===================

/**
 * Get all clues for an adventure
 */
export function useAdventureClues(adventureId: string | null) {
  return useQuery({
    queryKey: CLUE_QUERY_KEYS.list(adventureId || ''),
    queryFn: () => cluesApi.getClues(adventureId!),
    enabled: !!adventureId,
  })
}

/**
 * Get clue settings for an adventure
 */
export function useClueSettings(adventureId: string | null) {
  return useQuery({
    queryKey: CLUE_QUERY_KEYS.settings(adventureId || ''),
    queryFn: () => cluesApi.getSettings(adventureId!),
    enabled: !!adventureId,
  })
}

// ===================
// Mutation Hooks
// ===================

/**
 * Create a new clue
 */
export function useCreateClue(adventureId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateClueRequest) => {
      if (!adventureId) throw new Error('Adventure ID is required')
      return cluesApi.createClue(adventureId, data)
    },
    onSuccess: () => {
      if (adventureId) {
        queryClient.invalidateQueries({ queryKey: CLUE_QUERY_KEYS.list(adventureId) })
      }
    },
  })
}

/**
 * Update an existing clue
 */
export function useUpdateClue(adventureId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ clueId, data }: { clueId: string; data: UpdateClueRequest }) => {
      if (!adventureId) throw new Error('Adventure ID is required')
      return cluesApi.updateClue(adventureId, clueId, data)
    },
    onSuccess: () => {
      if (adventureId) {
        queryClient.invalidateQueries({ queryKey: CLUE_QUERY_KEYS.list(adventureId) })
      }
    },
  })
}

/**
 * Delete a clue
 */
export function useDeleteClue(adventureId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (clueId: string) => {
      if (!adventureId) throw new Error('Adventure ID is required')
      return cluesApi.deleteClue(adventureId, clueId)
    },
    onSuccess: () => {
      if (adventureId) {
        queryClient.invalidateQueries({ queryKey: CLUE_QUERY_KEYS.list(adventureId) })
      }
    },
  })
}

/**
 * Update clue settings
 */
export function useUpdateClueSettings(adventureId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: Partial<ClueSettingsDto>) => {
      if (!adventureId) throw new Error('Adventure ID is required')
      return cluesApi.updateSettings(adventureId, data)
    },
    onSuccess: () => {
      if (adventureId) {
        queryClient.invalidateQueries({ queryKey: CLUE_QUERY_KEYS.settings(adventureId) })
      }
    },
  })
}
